'use strict';
// Pure record-key helpers for the batch care-plan/billing lane. CommonJS so Jest
// can require it directly (the ESM orchestrator batch-careplan.js re-exports
// recordKeyFor from here). The `record_key` is patient-scoped
// (<patientId>:<monthStr>:<type>); the server prepends practice scoping via the
// unique index, so the client key carries no tenant id. No React/fetch/network.

// Build the client-side record_key for a (patient, month, type) triple.
function recordKeyFor(patientId, monthStr, type) {
  return patientId + ':' + monthStr + ':' + type;
}

// Parse a record_key back into its parts. type = parts.slice(2).join(':') so a
// colon inside the type (defensive — current types never contain one) survives
// the round-trip rather than being truncated.
function parseRecordKey(key) {
  const parts = String(key == null ? '' : key).split(':');
  return { patientId: parts[0], monthStr: parts[1], type: parts.slice(2).join(':') };
}

// Stable sort so every care_plan row materializes before any billing row. The
// care plan for this exact month must already be SAVED to the store when the
// billing note materializes, because the billing materializers RECOMPUTE the
// "care plan on file" boolean from the live store at materialize time (see
// carePlanOnFileForMonth + materializeBhiBillingResult). This matches the
// realtime sequencing (care plan saved, THEN billing note built — and the
// billing note reads the now-current store). Array#sort is stable (ES2019+),
// so rows within each group keep their original order. Non-mutating (operates
// on a copy).
function orderResultsCarePlanFirst(rows) {
  return rows.slice().sort(function (a, b) {
    const ca = parseRecordKey(a.record_key).type === 'care_plan' ? 0 : 1;
    const cb = parseRecordKey(b.record_key).type === 'care_plan' ? 0 : 1;
    return ca - cb;
  });
}

// Recompute the BHI "care plan on file" boolean from a live care-plan store,
// replicating EXACTLY the realtime BHI semantics in
// billing-generate.js#generateAndSaveBhiBillingNote:
//   const carePlanText = (cpHistory[patientId] && cpHistory[patientId][monthStr]) || '';
//   const carePlanOnFile = typeof carePlanText === 'string' && carePlanText.trim().length > 0;
// i.e. exact-month presence (NOT <= month) with non-empty trimmed text.
//
// `cpHistory` is the { [patientId]: { [yyyy-mm]: planText } } map (the
// `care_plan` slice of ccmApi.listCarePlans()). The batch lane freezes
// carePlanOnFile at COLLECT time (before any plan is saved this run); the
// materializer calls this against the LIVE store AFTER orderResultsCarePlanFirst
// has saved the same-run care plan, so the recompute yields the realtime value.
function carePlanOnFileForMonth(cpHistory, patientId, monthStr) {
  const carePlanText = (cpHistory && cpHistory[patientId] && cpHistory[patientId][monthStr]) || '';
  return typeof carePlanText === 'string' && carePlanText.trim().length > 0;
}

module.exports = { recordKeyFor, parseRecordKey, orderResultsCarePlanFirst, carePlanOnFileForMonth };
